import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axiosConfig';
import { FiRefreshCw, FiTrash2, FiAlertTriangle, FiSearch, FiX, FiTrash } from 'react-icons/fi';
import './AdminRateLimits.css';

function AdminRateLimits() {
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchViolations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get('/api/admin/rate-limit-violations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data.items || [];
      setItems(data);
      setFiltered(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load rate-limit violations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchViolations();
  }, [fetchViolations]);

  // Live search filter
  useEffect(() => {
    if (!search.trim()) {
      setFiltered(items);
      return;
    }
    const q = search.toLowerCase();
    setFiltered(
      items.filter(row =>
        Object.values(row).some(v => String(v).toLowerCase().includes(q))
      )
    );
  }, [search, items]);

  const handleDelete = async (violationId) => {
    if (!window.confirm(`Clear this record?`)) return;
    setDeletingId(violationId);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/admin/rate-limit-violations/${encodeURIComponent(violationId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(prev => prev.filter(r => r.violationId !== violationId));
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete ALL ${items.length} violation records? This cannot be undone.`)) return;
    setDeletingAll(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.delete('/api/admin/rate-limit-violations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems([]);
      setFiltered([]);
      alert(`Deleted ${res.data.deleted || 0} records.`);
    } catch (err) {
      alert('Delete All failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeletingAll(false);
    }
  };

  // Derive columns dynamically – pin 'ip' as the very first column
  const columns = items.length > 0
    ? (() => {
        const all = Object.keys(items[0]).filter(k => k !== '__key');
        const rest = all.filter(k => k !== 'ip');
        return all.includes('ip') ? ['ip', ...rest] : all;
      })()
    : [];

  const formatCell = (value) => {
    if (value === null || value === undefined) return <span style={{ color: '#6b7280' }}>—</span>;
    if (typeof value === 'object') return <code style={{ fontSize: '0.75rem' }}>{JSON.stringify(value)}</code>;
    // Try to format epoch timestamps
    if (typeof value === 'number' && value > 1_000_000_000 && value < 9_999_999_999) {
      const d = new Date(value * 1000);
      if (!isNaN(d)) return <span title={String(value)}>{d.toLocaleString()}</span>;
    }
    // ISO strings
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return <span title={value}>{new Date(value).toLocaleString()}</span>;
    }
    return String(value);
  };

  // Use violationId (the DynamoDB PK) as the row key for all delete operations
  const getRowKey = (row) => row.violationId || row.ip || row.id || JSON.stringify(row);

  return (
    <div className="rate-limits-page">
      {/* Header */}
      <div className="rate-limits-header">
        <div className="rate-limits-title">
          <FiAlertTriangle className="title-icon" />
          <div>
            <h1>Rate-Limit Violations</h1>
            <p className="subtitle">
              {items.length} record{items.length !== 1 ? 's' : ''} in table
              {lastRefresh && (
                <span className="last-refresh"> · Last refreshed {lastRefresh.toLocaleTimeString()}</span>
              )}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn-refresh"
            onClick={fetchViolations}
            disabled={loading}
            title="Refresh"
          >
            <FiRefreshCw className={loading ? 'spin' : ''} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {items.length > 0 && (
            <button
              className="btn-delete"
              onClick={handleDeleteAll}
              disabled={deletingAll}
              title="Delete all records"
              style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}
            >
              <FiTrash />
              {deletingAll ? 'Deleting…' : `Delete All (${items.length})`}
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="error-banner">
          <FiAlertTriangle /> {error}
          <button onClick={() => setError('')}><FiX /></button>
        </div>
      )}

      {/* Search bar */}
      <div className="search-bar">
        <FiSearch className="search-icon" />
        <input
          type="text"
          placeholder="Search anything…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="clear-search" onClick={() => setSearch('')}>
            <FiX />
          </button>
        )}
      </div>

      {/* Stats strip */}
      <div className="stats-strip">
        <div className="stat-card">
          <span className="stat-value">{items.length}</span>
          <span className="stat-label">Total Records</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{filtered.length}</span>
          <span className="stat-label">Showing</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{columns.length}</span>
          <span className="stat-label">Columns</span>
        </div>
      </div>

      {/* Table */}
      {loading && items.length === 0 ? (
        <div className="loading-state">Loading violations…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <FiAlertTriangle size={32} />
          <p>No rate-limit violations found.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="violations-table">
            <thead>
              <tr>
                <th>#</th>
                {columns.map(col => (
                  <th key={col}>{col}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, idx) => {
                const rowKey = getRowKey(row);
                return (
                  <tr key={rowKey} className={idx % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td className="row-num">{idx + 1}</td>
                    {columns.map(col => (
                      <td key={col} className={col === 'ip' ? 'cell-ip' : ''}>
                        {formatCell(row[col])}
                      </td>
                    ))}
                    <td>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(row.violationId || rowKey)}
                        disabled={deletingId === (row.violationId || rowKey)}
                        title="Delete record"
                      >
                        <FiTrash2 />
                        {deletingId === (row.violationId || rowKey) ? 'Deleting…' : 'Clear'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="no-results">No records match "{search}"</div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminRateLimits;
